<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { get } from 'svelte/store';
  import { flashcardActions, selectedTargetLanguage, filteredFlashcards } from './stores/flashcards';
  import { authActions, isAuthenticated, isLoading as authLoading } from './stores/auth';
  import { navigationState } from './stores/navigation';
  import { syncService } from './lib/sync-service';
  import { Capacitor } from '@capacitor/core';
  import { initNotificationListeners, updateNotificationSchedule } from './lib/notifications';
  import { userLanguagesActions, activeLanguage } from './stores/userLanguages';

  import Dashboard from './components/Dashboard.svelte';
  import LearningScreen from './components/LearningScreen.svelte';
  import Settings from './components/Settings.svelte';
  import FlashcardList from './components/FlashcardList.svelte';
  import FlashcardDialog from './components/dialogs/FlashcardDialog.svelte';
  import FlashcardViewerDialog from './components/dialogs/FlashcardViewerDialog.svelte';
  import FSRSReviewDialog from './components/dialogs/FSRSReviewDialog.svelte';
  import ExerciseDialog from './components/dialogs/ExerciseDialog.svelte';
  import PhotoCapture from './components/PhotoCapture.svelte';
  import TextsList from './components/TextsList.svelte';
  import TextNew from './components/TextNew.svelte';
  import TextView from './components/TextView.svelte';
  import Speak from './components/Speak.svelte';
  import Login from './components/Login.svelte';
  import Onboarding from './components/Onboarding.svelte';
  import BottomNavigation from './components/BottomNavigation.svelte';
  import LoadingOverlay from './components/LoadingOverlay.svelte';
  import { navigationActions } from './stores/navigation';
  import { userSettings, settingsActions, isLoadingSettings } from './stores/settings';
  import { setAppLocale } from './lib/i18n';

  let isLoading = $state(true);
  let isInitialized = $state(false); // Flag to prevent re-initialization

  // Dialog visibility states
  const showAddFlashcardModal = $derived($navigationState.currentView === 'add-flashcard');
  const showEditFlashcardModal = $derived($navigationState.currentView === 'edit-flashcard');
  const showFlashcardViewerDialog = $derived($navigationState.currentView === 'flashcard-viewer');
  const showFSRSReviewDialog = $derived($navigationState.currentView === 'fsrs-review');
  const showExerciseDialog = $derived($navigationState.currentView === 'exercise-mode');

  // Check if any dialog is open
  const isAnyDialogOpen = $derived(
    showAddFlashcardModal ||
    showEditFlashcardModal ||
    showFlashcardViewerDialog ||
    showFSRSReviewDialog ||
    showExerciseDialog
  );

  const resolvedView = $derived(
    isAnyDialogOpen
      ? $navigationState.previousView ?? 'dashboard'
      : $navigationState.currentView
  );

  // When showing modals, use previousParams for the background view, but keep current params for modal
  const resolvedParams = $derived(
    isAnyDialogOpen ? $navigationState.previousParams : $navigationState.params
  );

  // Params for the modal itself
  const modalParams = $derived(
    isAnyDialogOpen ? $navigationState.params : undefined
  );

  onMount(async () => {
    console.log('🔄 App.svelte onMount called, isInitialized:', isInitialized);
    
    // Prevent re-initialization if already initialized
    if (isInitialized) {
      console.log('✅ App already initialized, skipping re-initialization');
      return;
    }

    try {
      console.log('🚀 Starting app initialization...');
      
      // Initialize notification listeners for mobile
      if (Capacitor.isNativePlatform()) {
        initNotificationListeners();
      }

      // Initialize auth first
      await authActions.initialize();

      // Wait for auth state to be determined (avoid synchronous subscribe callbacks
      // that may run before local variables are initialized)
      await new Promise<void>((resolve) => {
        let unsubscribe: (() => void) | null = null;

        unsubscribe = authLoading.subscribe((loading) => {
          if (!loading) {
            unsubscribe?.();
            resolve();
          }
        });
      });

      // After auth resolution, check authentication and load data if needed
      if (get(isAuthenticated)) {
        // Load user settings first
        await settingsActions.loadSettings();

        // Load user languages
        await userLanguagesActions.loadUserLanguages();

        // Load flashcards data
        await flashcardActions.loadData();

        // Set selected language to user's active language
        const active = get(activeLanguage);
        if (active && active.language) {
          flashcardActions.selectTargetLanguage({
            id: active.language.id,
            name: active.language.name,
            code: active.language.code,
          });
        }

        // Start auto-sync in background (every 5 minutes)
        syncService.startAutoSync(5 * 60 * 1000);

        // Set up notification schedule based on user settings
        if (Capacitor.isNativePlatform()) {
          await updateNotificationSchedule();
        }
      }
      isLoading = false;
      isInitialized = true;

      // Handle visibility changes to prevent unnecessary reloads
      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.log('👁️ App hidden - pausing sync');
          syncService.stopAutoSync();
        } else {
          console.log('👁️ App visible - resuming sync (no reload should happen!)');
          if (get(isAuthenticated)) {
            syncService.startAutoSync(5 * 60 * 1000);
          }
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      console.log('✅ Visibility listener registered');

      // Cleanup listener on unmount
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        syncService.stopAutoSync();
      };
    } catch (error) {
      console.error('App initialization error:', error);
      isLoading = false;
    }
  });

  $effect(() => {
    if ($userSettings?.preferred_language) {
      setAppLocale($userSettings.preferred_language);
    }
  });

  // Reset category when returning to learn view
  $effect(() => {
    if ($navigationState.currentView === 'vocabulary') {
      flashcardActions.selectCategory(null);
    }
  });

  // Apply theme reactivity
  $effect(() => {
    if ($isAuthenticated && $userSettings?.theme) {
      settingsActions.applyTheme($userSettings.theme);
    } else if (!$isAuthenticated) {
      // Apply default theme for unauthenticated users
      settingsActions.applyTheme('light');
    }
  });
</script>

{#if isLoading || $authLoading || $isLoadingSettings}
  <!-- Loading Screen -->
  <div class="min-h-screen bg-gradient-primary flex items-center justify-center relative overflow-hidden">
    <div class="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.18),_transparent_60%)]"></div>
    <div class="text-center relative z-10">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-white/70 mx-auto mb-4"></div>
      <p class="text-sm uppercase tracking-[0.3em] text-white/70">{$_('messages.loading')}</p>
    </div>
  </div>
{:else if !$isAuthenticated}
  <!-- Login Screen -->
  <Login />
{:else if $isAuthenticated && !$userSettings?.onboarding_completed}
  <!-- Onboarding Flow -->
  <Onboarding onComplete={() => {
    // Settings are already updated by Onboarding component
    // Reload to reflect changes and navigate to home
    isLoading = true;
    settingsActions.loadSettings().finally(() => {
      isLoading = false;
    });
  }} />
{:else}
  <!-- Authenticated App -->
  <main class="app-shell min-h-screen pb-24">
    {#if resolvedView === 'dashboard'}
      <Dashboard />
    {:else if resolvedView === 'vocabulary'}
      <LearningScreen />
    {:else if resolvedView === 'settings'}
      <Settings />
    {:else if resolvedView === 'flashcard-list'}
      <FlashcardList onBack={() => navigationActions.goBack()} onAdd={() => navigationActions.addFlashcard()} onEdit={(id) => navigationActions.editFlashcard(id)} />
    {:else if resolvedView === 'photo-capture'}
      <PhotoCapture onBack={() => navigationActions.goBack()} />
    {:else if resolvedView === 'texts-list'}
      <TextsList />
    {:else if resolvedView === 'texts-new'}
      <TextNew />
    {:else if resolvedView === 'text-view'}
      <TextView textId={resolvedParams?.textId ?? ''} />
    {:else if resolvedView === 'speak'}
      <Speak />
    {:else}
      <Dashboard />
    {/if}

    <BottomNavigation />
  </main>
{/if}

<FlashcardDialog
  open={showAddFlashcardModal || showEditFlashcardModal}
  mode={showEditFlashcardModal ? 'edit' : 'add'}
  cardId={modalParams?.cardId ?? ''}
  onClose={() => navigationActions.goBack()}
/>

<FlashcardViewerDialog
  open={showFlashcardViewerDialog}
  flashcardList={$filteredFlashcards}
  startIndex={modalParams?.startIndex}
  onClose={() => navigationActions.goBack()}
  onEdit={(id) => navigationActions.editFlashcard(id)}
/>

<FSRSReviewDialog
  open={showFSRSReviewDialog}
  flashcardList={$filteredFlashcards}
  onClose={() => navigationActions.goBack()}
  onComplete={() => navigationActions.goHome()}
/>

<ExerciseDialog
  open={showExerciseDialog}
  flashcards={$filteredFlashcards}
  config={{
    ...(modalParams?.totalExercises !== undefined && { totalExercises: modalParams.totalExercises }),
    ...(modalParams?.modeDistribution !== undefined && { modeDistribution: modalParams.modeDistribution }),
    ...(modalParams?.useFSRS !== undefined && { useFSRS: modalParams.useFSRS }),
    ...(modalParams?.saveFSRSReview !== undefined && { saveFSRSReview: modalParams.saveFSRSReview }),
    ...(modalParams?.userId !== undefined && { userId: modalParams.userId }),
    ...(modalParams?.targetLanguageId !== undefined && { targetLanguageId: modalParams.targetLanguageId }),
  }}
  onClose={() => navigationActions.goBack()}
/>

<LoadingOverlay />
